import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { UserRound } from "lucide-react";
import { PanelAddButton, PanelScopeToggle } from "../components/PanelListHeader";

const MAX_FEED = 200;

export const isActiveDevice = (device, activeSessions = {}) => !!(
  device?.heartbeat_status === "online"
  || activeSessions[device?.device_id]
);

function formatTime(iso) {
  try { return new Date(iso).toLocaleTimeString("hu-HU", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return ""; }
}

const BADGE = {
  loctag:        { cls: "bg-teal-900 text-teal-300",     label: "TAG"    },
  login:         { cls: "bg-slate-700 text-slate-400",   label: "DEV"    },
  web_login:     { cls: "bg-blue-900 text-blue-300",     label: "WEB ▶"  },
  web_logout:    { cls: "bg-blue-950 text-blue-500",     label: "WEB ■"  },
  device_login:  { cls: "bg-indigo-900 text-indigo-300", label: "DEV ▶"  },
  device_logout: { cls: "bg-indigo-950 text-indigo-500", label: "DEV ■"  },
  other:         { cls: "bg-slate-800 text-slate-500",   label: "EVT"    },
};

function FeedRow({ item }) {
  const badge = BADGE[item.type] || BADGE.other;
  return (
    <div
      className="feed-row flex items-start gap-1.5 px-1.5 py-0.5 border-b border-slate-800 text-[0.58rem] font-mono leading-tight"
      style={{ animation: "feedSlideIn 0.25s ease-out both" }}
    >
      <span className="text-slate-600 shrink-0 w-14 pt-px">{item.time}</span>
      <span className={`shrink-0 px-1 rounded font-bold tracking-wider ${badge.cls}`}>{badge.label}</span>
      <span className="flex-1 min-w-0">
        {item.type === "loctag" && (
          <>
            <span className="text-green-400 font-bold">{item.location || "?"}</span>
            {item.user_name && <span className="text-slate-400 ml-1">· {item.user_name}</span>}
            {item.tag && <span className="text-slate-500 ml-1">#{item.tag}</span>}
            {item.read_count && <span className="text-yellow-600 ml-1">×{item.read_count}</span>}
            {item.device && <span className="text-slate-600 ml-1">· {item.device}</span>}
          </>
        )}
        {(item.type === "login") && (
          <span className="text-slate-400">{item.device}</span>
        )}
        {(item.type === "web_login" || item.type === "web_logout") && (
          <span className="text-blue-300">{item.user_name || item.raw}</span>
        )}
        {(item.type === "device_login" || item.type === "device_logout") && (
          <>
            <span className="text-indigo-300">{item.user_name || item.raw}</span>
            {item.device && <span className="text-slate-600 ml-1">· {item.device}</span>}
          </>
        )}
        {item.type === "other" && (
          <span className="text-slate-500">{item.raw}</span>
        )}
      </span>
    </div>
  );
}

const IcleanViewReaders = ({
  selectedCompanyId,
  readerMode,
  setReaderMode,
  editLoctagMode,
  setEditLoctagMode,
  addMode,
  setAddMode,
  setTrackedDevices,
  loctags,
  locations,
  deviceEvents,
  setDeviceEvents,
  onOpenManualRegistration,
  deviceListRefreshTrigger,
  onHeightChange,
  // Loctag registration props
  registrationDevice,
  isReading,
  scannedTags,
  registrationStep,
  onStartReading,
  onStopReading,
  onDeviceSelectedDirect,
  onRegisterTag,
  onConfirmLoctag,
  embedded = false,
  // Ha meg van adva, a loctag-regisztráció és a live feed ezekbe a panelszekciókba kerül.
  loctagTarget = null,
  feedTarget = null,
  rootLocations = [],
}) => {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [expandedDevices, setExpandedDevices] = useState(new Set());
  const [currentDate, setCurrentDate] = useState(new Date().toISOString().split("T")[0]);
  const [liveFeed, setLiveFeed] = useState([]);
  const feedRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const devicesRef = useRef([]);
  const menuRef = useRef(null);
  const initialFeedLoaded = useRef(false);
  const [remoteTestDevices, setRemoteTestDevices] = useState(new Set());
  const [testTerminals, setTestTerminals] = useState({});
  const [activeDeviceSessions, setActiveDeviceSessions] = useState({});
  const [deviceScope, setDeviceScope] = useState("active");
  const testStreamsRef = useRef({});

  useEffect(() => { devicesRef.current = devices; }, [devices]);

  useEffect(() => {
    const deviceIds = devices.map((device) => device.device_id);
    setTrackedDevices((current) => (
      current.length === deviceIds.length
      && current.every((deviceId, index) => deviceId === deviceIds[index])
        ? current
        : deviceIds
    ));
  }, [devices, setTrackedDevices]);

  const fetchActiveDeviceSessions = useCallback(async () => {
    try {
      const response = await fetch("/api/device-sessions", { credentials: "include" });
      const data = await response.json();
      const active = {};
      (data.sessions || []).forEach((session) => {
        if (!session.logout_at && !active[session.device_id]) active[session.device_id] = session;
      });
      setActiveDeviceSessions(active);
    } catch {}
  }, []);

  useEffect(() => {
    if (!readerMode || !selectedCompanyId) return;
    fetchActiveDeviceSessions();
    const interval = setInterval(fetchActiveDeviceSessions, 5000);
    return () => clearInterval(interval);
  }, [readerMode, selectedCompanyId, fetchActiveDeviceSessions]);

  const clearTestTerminal = (deviceId) => {
    setTestTerminals(prev => ({ ...prev, [deviceId]: [] }));
  };

  const toggleRemoteTest = async (deviceId) => {
    const isActive = remoteTestDevices.has(deviceId);
    const newActive = !isActive;

    // State frissítés azonnal, fetch előtt
    setRemoteTestDevices(prev => {
      const next = new Set(prev);
      if (newActive) next.add(deviceId); else next.delete(deviceId);
      return next;
    });

    if (newActive) {
      setTestTerminals(prev => ({ ...prev, [deviceId]: [] }));
      const es = new EventSource(`/api/device/test-stream/${deviceId}`, { withCredentials: true });
      testStreamsRef.current[deviceId] = es;
      es.onmessage = (e) => {
        if (e.data === "connected") return;
        try {
          const d = JSON.parse(e.data);
          const { epc, rssi, ts, antenna, mmcx_port } = d;
          const eventId = `${Date.now()}-${Math.random()}`;
          setTestTerminals(prev => {
            const events = [...(prev[deviceId] || []), {
              id: eventId,
              epc,
              rssi,
              ts,
              antenna,
              mmcx_port,
              flash: true,
            }];
            return { ...prev, [deviceId]: events.slice(-200) };
          });
          setTimeout(() => {
            setTestTerminals(prev => {
              const events = (prev[deviceId] || []).map(ev =>
                ev.id === eventId ? { ...ev, flash: false } : ev
              );
              return { ...prev, [deviceId]: events };
            });
          }, 400);
        } catch {}
      };
    } else {
      if (testStreamsRef.current[deviceId]) {
        testStreamsRef.current[deviceId].close();
        delete testStreamsRef.current[deviceId];
      }
      setTestTerminals(prev => { const n = { ...prev }; delete n[deviceId]; return n; });
    }

    // Backend értesítése (tőle független a UI)
    try {
      await fetch("/api/device/remote-test/set", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ device_id: deviceId, active: newActive }),
        credentials: "include",
      });
    } catch (e) {
      console.warn("Remote test set failed:", e);
    }
  };

  const pushFeed = useCallback((item) => {
    setLiveFeed(prev => [...prev, item].slice(-MAX_FEED));
    // Auto-scroll: csak ha a user az alján van
    requestAnimationFrame(() => {
      const el = feedRef.current;
      if (el && isAtBottomRef.current) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }, []);

  // Scroll tracker: frissítjük isAtBottomRef-et amikor a user görget
  useEffect(() => {
    const el = feedRef.current;
    if (!el) return;
    const onScroll = () => {
      isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [feedTarget]);

  // ── Loctag section ───────────────────────────────────────────────────────────
  const [backendReadActive, setBackendReadActive] = useState(false);
  const [tagValidation, setTagValidation] = useState({ text: "---", color: "text-gray-400", type: "empty" });

  useEffect(() => {
    const validateTag = async () => {
      if (!scannedTags || scannedTags.length === 0) { setTagValidation({ text: "---", color: "text-gray-400", type: "empty" }); return; }
      if (scannedTags.length > 1 || scannedTags[0] === "failure more tag") { setTagValidation({ text: "Error – multiple tags!", color: "text-red-400 font-bold", type: "error" }); return; }
      const tag = scannedTags[0];
      if (tag === "---") { setTagValidation({ text: "---", color: "text-gray-400", type: "empty" }); return; }
      try {
        const res = await fetch("/location_tags", { credentials: "include" });
        const data = await res.json();
        if (data.success && data.location_tags) {
          const already = data.location_tags.some((t) => t.rfid_id === tag);
          setTagValidation(already
            ? { text: "Already registered", color: "text-red-400 font-bold", type: "already_registered" }
            : { text: tag, color: "text-teal-300 font-bold break-all", type: "valid" });
        }
      } catch { setTagValidation({ text: tag, color: "text-teal-300 font-bold break-all", type: "valid" }); }
    };
    validateTag();
  }, [scannedTags]);

  useEffect(() => {
    if (!registrationDevice) { setBackendReadActive(false); return; }
    const deviceId = localStorage.getItem("registration_device_id");
    if (!deviceId) return;
    const iv = setInterval(async () => {
      try { const r = await fetch(`/api/device/read-command/${deviceId}`); const d = await r.json(); if (d.success) setBackendReadActive(d.active); } catch {}
    }, 200);
    return () => clearInterval(iv);
  }, [registrationDevice]);

  // ── Devices section ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (menuRef.current && onHeightChange) onHeightChange(menuRef.current.offsetHeight);
  }, [devices, expandedDevices, editLoctagMode, onHeightChange]);

  useEffect(() => { if (selectedCompanyId && readerMode) fetchDevices(); }, [selectedCompanyId, readerMode, deviceListRefreshTrigger]);

  useEffect(() => {
    if (!readerMode || !selectedCompanyId) return;
    const es = new EventSource("/api/devices/status-stream", { withCredentials: true });
    es.addEventListener("device_status", (e) => {
      const { device_id, status } = JSON.parse(e.data);
      setDevices((prev) => prev.map((d) => d.device_id === device_id ? { ...d, heartbeat_status: status } : d));
    });
    es.onerror = () => es.close();
    return () => es.close();
  }, [readerMode, selectedCompanyId]);

  useEffect(() => {
    if (!readerMode) return;
    const es = new EventSource("/api/events/stream", { withCredentials: true });
    es.addEventListener("new_event", (e) => {
      const ev = JSON.parse(e.data);
      setDeviceEvents((prev) => ({ ...prev, [ev.device_id]: [ev, ...(prev[ev.device_id] || [])] }));
      // Live feed
      const deviceName = devicesRef.current.find(d => d.device_id === ev.device_id)?.device_name || ev.device_id;
      const t = formatTime(ev.event_time);
      const id = Date.now() + Math.random();
      if (ev.event_type === "LOCTAG_READ") {
        pushFeed({ id, type: "loctag",         time: t, location: ev.location_name || null, tag: ev.loctag_id || null, device: deviceName, user_name: ev.user_name || null, read_count: ev.read_count || null });
      } else if (ev.event_type === "LOGIN" || ev.event_type === "HEARTBEAT") {
        pushFeed({ id, type: "login",          time: t, device: deviceName });
      } else if (ev.event_type === "WEB_LOGIN") {
        pushFeed({ id, type: "web_login",      time: t, user_name: ev.user_name || String(ev.user_id) });
      } else if (ev.event_type === "WEB_LOGOUT") {
        pushFeed({ id, type: "web_logout",     time: t, user_name: ev.user_name || String(ev.user_id) });
      } else if (ev.event_type === "DEVICE_LOGIN") {
        pushFeed({ id, type: "device_login",   time: t, user_name: ev.user_name || String(ev.user_id), device: deviceName });
        fetchActiveDeviceSessions();
      } else if (ev.event_type === "DEVICE_LOGOUT") {
        pushFeed({ id, type: "device_logout",  time: t, user_name: ev.user_name || String(ev.user_id), device: deviceName });
        fetchActiveDeviceSessions();
      } else {
        pushFeed({ id, type: "other",          time: t, raw: ev.event_type, device: deviceName });
      }
    });
    es.onerror = () => es.close();
    return () => es.close();
  }, [readerMode, pushFeed, fetchActiveDeviceSessions]);

  useEffect(() => {
    if (!readerMode || devices.length === 0) return;
    const today = new Date().toISOString().split("T")[0];
    devices.forEach((d) => fetchDeviceEvents(d.device_id, today));
  }, [readerMode, devices]);

  // Kezdeti live feed betöltés: panel megnyitásakor a mai legutóbbi eseményeket tölti be
  useEffect(() => {
    if (!readerMode) { initialFeedLoaded.current = false; return; }
    if (initialFeedLoaded.current || devices.length === 0) return;
    initialFeedLoaded.current = true;
    const today = new Date().toISOString().split("T")[0];
    (async () => {
      try {
        const r = await fetch(`/api/v1/events?date=${today}&limit=50`, { credentials: "include" });
        const d = await r.json();
        if (d.success && d.events?.length > 0) {
          const deviceMap = Object.fromEntries(devices.map(dev => [dev.device_id, dev.device_name || dev.device_id]));
          const items = [...d.events].reverse().map((ev) => ({
            id: `h-${ev.event_id}`,
            type: ev.event_type === "LOCTAG_READ" ? "loctag" : "other",
            time: formatTime(ev.event_time),
            location: ev.location_name || null,
            tag: ev.loctag_id || null,
            device: deviceMap[ev.device_id] || ev.device_id,
            read_count: ev.read_count || null,
            raw: ev.event_type,
          }));
          setLiveFeed(items);
        }
      } catch {}
    })();
  }, [readerMode, devices]);

  useEffect(() => {
    if (!readerMode || !selectedDevice) return;
    fetchDeviceEvents(selectedDevice.device_id, currentDate);
  }, [selectedDevice, currentDate]);

  const fetchDevices = async () => {
    try { const r = await fetch("/api/v1/devices", { credentials: "include" }); const d = await r.json(); if (d.success) setDevices(d.devices); } catch {}
  };

  const fetchDeviceEvents = async (deviceId, date = currentDate) => {
    try {
      const r = await fetch(`/api/v1/events?device_id=${deviceId}&date=${date}&limit=50`, { credentials: "include" });
      const d = await r.json();
      if (d.success) setDeviceEvents((prev) => ({ ...prev, [deviceId]: d.events || [] }));
    } catch {}
  };

  // Tracker benzinkút-hozzárendelése: üres = minden rootnál látszik.
  const setDeviceRoot = async (device, rootId) => {
    const value = rootId === "" ? null : Number(rootId);
    try {
      const response = await fetch(`/api/v1/devices/${encodeURIComponent(device.device_id)}/root`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ root_location_id: value }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) throw new Error(data.detail || "Saving the root location failed.");
      setDevices((prev) => prev.map((d) => (d.device_id === device.device_id ? { ...d, root_location_id: data.root_location_id } : d)));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeviceClick = (device) => {
    if (expandedDevices.has(device.device_id)) {
      setExpandedDevices((prev) => { const s = new Set(prev); s.delete(device.device_id); return s; });
      setSelectedDevice(null);
    } else {
      setExpandedDevices(new Set([device.device_id]));
      setSelectedDevice(device);
      fetchDeviceEvents(device.device_id);
    }
  };

  const onlineDevices = devices.filter((d) => d.heartbeat_status === "online");
  const visibleDevices = deviceScope === "active"
    ? devices.filter((device) => isActiveDevice(device, activeDeviceSessions))
    : devices;

  if (!readerMode) return null;

  const loctagSection = (
      <div className={`space-y-2 text-xs text-slate-100 ${loctagTarget ? "" : "mb-4"}`}>
        {!loctagTarget && <div className="text-[0.6rem] text-slate-400 uppercase tracking-wide">Loctag</div>}

        {/* Registration flow (shown when addMode/registrationDevice active) */}
        {(addMode || registrationDevice) && (
          <div className="space-y-2 border border-slate-700 rounded p-2 bg-slate-800/40">
            {addMode && !registrationDevice && (
              <>
                <div className="text-[0.6rem] text-slate-400">Select device:</div>
                {onlineDevices.length === 0 && <div className="text-[0.6rem] text-slate-500">No online devices</div>}
                {onlineDevices.map((d) => (
                  <button key={d.device_id}
                    onClick={() => onDeviceSelectedDirect && onDeviceSelectedDirect(d.device_id, d.device_name || d.device_id)}
                    className="w-full text-left px-2 py-1.5 rounded border border-slate-600 bg-slate-800 hover:bg-slate-700 text-[0.65rem] text-slate-200"
                  >{d.device_name || d.device_id}</button>
                ))}
              </>
            )}
            {registrationDevice && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 px-2 py-1.5 rounded border border-slate-600 bg-slate-900 text-[0.65rem] font-mono text-center">
                    <span className={tagValidation.color}>{tagValidation.text}</span>
                  </div>
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${backendReadActive ? "bg-green-500" : "bg-slate-600"}`}
                       title={backendReadActive ? "READ active" : "READ inactive"}/>
                </div>
                <button
                  className={`w-full px-2 py-1.5 rounded text-white font-bold text-[0.65rem] select-none ${isReading ? "bg-red-600 animate-pulse" : "bg-blue-600 hover:bg-blue-500"}`}
                  style={{ userSelect: "none" }}
                  onMouseDown={() => onStartReading && onStartReading()}
                  onMouseUp={() => onStopReading && onStopReading()}
                  onTouchStart={() => onStartReading && onStartReading()}
                  onTouchEnd={() => onStopReading && onStopReading()}
                >{isReading ? "READING..." : "READ"}</button>
                {registrationStep === "confirm" ? (
                  <button onClick={() => onConfirmLoctag && onConfirmLoctag()}
                    className="w-full px-2 py-1.5 rounded bg-teal-600 hover:bg-teal-500 text-white font-bold text-[0.65rem]">
                    LOCTAG FIX AND SAVE
                  </button>
                ) : (registrationStep === "placing" || tagValidation.type !== "valid" || isReading) ? (
                  <button disabled className="w-full px-2 py-1.5 rounded bg-slate-700 text-slate-500 font-bold text-[0.65rem] cursor-not-allowed">REGISTER</button>
                ) : (
                  <button onClick={() => onRegisterTag && onRegisterTag()}
                    className="w-full px-2 py-1.5 rounded bg-teal-600 hover:bg-teal-500 text-white font-bold text-[0.65rem]">REGISTER</button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Loctag Registration button – always at bottom of section */}
        {!addMode && !registrationDevice && (
          <button
            onClick={() => { if (setEditLoctagMode) setEditLoctagMode(true); if (setAddMode) setAddMode(true); }}
            className="w-full px-2 py-1 rounded border text-[0.7rem] bg-slate-800 text-slate-100 border-slate-600 hover:bg-slate-700"
          >
            + Loctag Registration
          </button>
        )}
      </div>
  );

  const feedSection = (
    <div className={feedTarget ? "" : "mt-4"}>
      <style>{`
        @keyframes feedSlideIn {
          from { opacity: 0; transform: translateY(6px); background: rgba(20,184,166,0.10); }
          to   { opacity: 1; transform: translateY(0);   background: transparent; }
        }
      `}</style>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[0.6rem] text-slate-400 uppercase tracking-wide font-semibold">Live feed</span>
        <span className={`w-2 h-2 rounded-full ${liveFeed.length > 0 ? "bg-green-500 animate-pulse" : "bg-slate-600"}`}/>
      </div>
      <div
        ref={feedRef}
        className="border border-slate-700 rounded bg-slate-950 overflow-y-auto"
        style={{ height: 280 }}
      >
        {liveFeed.length === 0 ? (
          <div className="flex items-center justify-center h-full text-[0.6rem] text-slate-700 font-mono">
            waiting for events…
          </div>
        ) : (
          liveFeed.map(item => <FeedRow key={item.id} item={item} />)
        )}
      </div>
    </div>
  );

  return (
    <div ref={menuRef} className={`block w-full text-slate-100 text-xs ${embedded ? "p-3" : "border-b border-slate-800 p-4"}`}>

      {/* ── Header ── */}
      {!embedded && <div className="flex justify-between items-center mb-3">
        <div className="text-[0.7rem] text-slate-200 font-semibold uppercase tracking-wide">Devices</div>
        <button
          onClick={() => { setReaderMode(false); if (setEditLoctagMode) setEditLoctagMode(false); if (setAddMode) setAddMode(false); }}
          className="w-5 h-5 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center text-slate-100 text-sm"
          title="Close"
        >×</button>
      </div>}

      {/* ── Loctag section ── */}
      {loctagTarget ? createPortal(loctagSection, loctagTarget) : loctagSection}

      {/* ── Device list ── */}
      <div className="space-y-2">
        {/* Section label + scope + add */}
        <div className="grid min-h-10 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 px-1">
          <span className="text-[0.7rem] font-semibold text-slate-300">
            Devices {devices.length > 0 ? `(${visibleDevices.length}${deviceScope === "active" ? `/${devices.length}` : ""})` : ""}
          </span>
          <PanelScopeToggle
            value={deviceScope}
            onChange={setDeviceScope}
            label="Devices filter"
          />
          <div className="flex justify-self-end">
            <PanelAddButton onClick={onOpenManualRegistration} label="New device" />
          </div>
        </div>

        {/* Device rows */}
        <div className="max-h-72 space-y-1 overflow-y-auto rounded-lg border border-slate-700 bg-slate-950/40 p-2">
          {visibleDevices.length === 0 && (
            <div className="text-[0.6rem] text-slate-500 px-1 py-1">
              {deviceScope === "active" ? "No active devices" : "No devices found"}
            </div>
          )}
          {visibleDevices.map((device) => (
            <div key={device.device_id}>
              <div className={`flex items-center gap-2 px-2 py-1 rounded border text-[0.65rem] cursor-pointer ${
                expandedDevices.has(device.device_id)
                  ? "bg-teal-900/30 border-teal-700 text-teal-100"
                  : "bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
              }`}>
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${device.heartbeat_status === "online" ? "bg-green-500 shadow-[0_0_4px_#22c55e]" : "bg-slate-600"}`}/>
                <button className="flex-1 text-left truncate" onClick={() => handleDeviceClick(device)}
                  title={device.last_heartbeat ? `Last heartbeat: ${new Date(device.last_heartbeat).toLocaleString("hu-HU")}` : "No heartbeat data"}>
                  {device.device_name || device.device_id}
                </button>
                {activeDeviceSessions[device.device_id] && (
                  <span className="flex max-w-24 shrink-0 items-center gap-1 rounded bg-teal-500/15 px-1.5 py-0.5 text-[0.5rem] text-teal-300" title={`${activeDeviceSessions[device.device_id].user_name} signed in`}>
                    <UserRound size={10} className="shrink-0" />
                    <span className="truncate">{activeDeviceSessions[device.device_id].user_name}</span>
                  </span>
                )}
                {device.heartbeat_status === "online" && (
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleRemoteTest(device.device_id); }}
                    title="Remote Test mode"
                    className={`shrink-0 px-1 py-0.5 rounded text-[0.5rem] font-bold tracking-wider border ${
                      remoteTestDevices.has(device.device_id)
                        ? "bg-yellow-500 border-yellow-400 text-black"
                        : "bg-slate-700 border-slate-600 text-slate-400 hover:bg-slate-600"
                    }`}
                  >
                    RT
                  </button>
                )}
              </div>
              {remoteTestDevices.has(device.device_id) && (
                <div className="ml-2 mt-1 mb-1 border border-yellow-700/50 rounded bg-black/80 font-mono text-[0.55rem]">
                  <div className="flex items-center justify-between px-2 py-0.5 border-b border-yellow-800/40">
                    <span className="text-yellow-400 font-bold">REMOTE TEST — {device.device_name || device.device_id}</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => clearTestTerminal(device.device_id)}
                        className="text-slate-400 hover:text-yellow-300 text-[0.6rem] border border-slate-600 hover:border-yellow-600 rounded px-1 py-0"
                      >Clear</button>
                      <button onClick={() => toggleRemoteTest(device.device_id)} className="text-slate-500 hover:text-red-400 text-xs">✕</button>
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto p-1.5 space-y-0.5">
                    {(testTerminals[device.device_id] || []).length === 0 && (
                      <div className="text-slate-600 italic">Waiting for tag reads...</div>
                    )}
                    {(testTerminals[device.device_id] || []).map((ev) => (
                      <div
                        key={ev.id}
                        style={{ transition: 'background-color 0.08s', backgroundColor: ev.flash ? 'rgba(234,179,8,0.25)' : 'transparent' }}
                        className="flex gap-2 items-center rounded px-1 text-slate-200"
                      >
                        <span className="shrink-0 text-slate-500 w-10">{ev.ts || ""}</span>
                        <span className="text-slate-200 tracking-wide flex-1">{ev.epc}</span>
                        {ev.rssi != null && (
                          <span className="shrink-0 text-cyan-400">{ev.rssi}</span>
                        )}
                        {ev.mmcx_port != null && (
                          <span className="shrink-0 text-slate-500">J{ev.mmcx_port}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {expandedDevices.has(device.device_id) && (
                <div className="ml-2 mt-1 mb-1 border border-slate-700 rounded p-1.5 bg-slate-900/60">
                  {rootLocations.length > 0 && (
                    <label className="mb-1.5 flex items-center gap-2 text-[0.6rem] text-slate-400">
                      <span>Root location</span>
                      <select
                        aria-label={`Root location of ${device.device_name || device.device_id}`}
                        value={device.root_location_id ?? ""}
                        onChange={(e) => setDeviceRoot(device, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="flex-1 rounded border border-slate-600 bg-slate-950 px-1.5 py-0.5 text-[0.65rem] text-slate-100"
                      >
                        <option value="">All (not assigned)</option>
                        {rootLocations.map((root) => <option key={root.id} value={root.id}>{root.name}</option>)}
                      </select>
                    </label>
                  )}
                  <div className="space-y-1 max-h-36 overflow-y-auto">
                    {deviceEvents[device.device_id]?.length > 0 ? (
                      deviceEvents[device.device_id].map((ev, i) => (
                        <div key={i} className="text-[0.55rem] text-slate-400 flex gap-2">
                          <span className="text-slate-500 shrink-0">
                            {new Date(ev.event_time).toLocaleTimeString("hu-HU", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                          {ev.location_name && <span className="text-teal-400">{ev.location_name}</span>}
                          {ev.loctag_id && <span className="text-slate-500 font-mono">#{ev.loctag_id}</span>}
                        </div>
                      ))
                    ) : (
                      <div className="text-[0.55rem] text-slate-600 text-center">No events today</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

      </div>

      {/* ── Live transaction feed ── */}
      {feedTarget ? createPortal(feedSection, feedTarget) : feedSection}
    </div>
  );
};

export default IcleanViewReaders;
